import { Component, EventEmitter, OnInit, Output, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTabsModule } from "@angular/material/tabs";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DocumentModel } from "../../models/document.model";
import { AbstractDocumentModel } from "../../models/abstract-document-model";
import { EditDocumentPopup, OpenDocumentPopupReason } from "../../models/edit-document-popup.model";
import { BaseDocumentsService } from "../../services/base-documents.service";
import { CerrixDropAreaComponent } from "../../cerrix-drop-area/cerrix-drop-area.component";
import { CerrixConfirmationButtonComponent } from "../../cerrix-confirmation-button/cerrix-confirmation-button.component";
import { IncidentDocumentsService } from "@app/incident/services/incident-documents.service";
import { CerrixEditDocumentHistoryItemComponent } from "../cerrix-edit-document-history-item/cerrix-edit-document-history-item.component";
import { Observable } from "rxjs";
import { CerrixAccordionGroupComponent } from "@app/shared/cerrix-accordion-group/cerrix-accordion-group.component";
import { DateTimeDisplayPipe } from "@app/shared/pipes/date-time-display.pipe";
import { DocumentConfig } from "@models/documents/DocumentConfig";
import { AbstractEditDocumentComponent } from "../cerrix-edit-document-abstract-component";
import { CerrixButtonComponent } from "@app/incident/components/src/lib/core/cerrix-button/cerrix-button.component";
import { CerrixTitleComponent } from "@app/incident/components/src/lib/core/cerrix-title/cerrix-title.component";
import { CerrixFieldWrapperComponent } from "@app/incident/components/src/lib/core/cerrix-field-wrapper/cerrix-field-wrapper.component";
import { CerrixSelectComponent } from "@app/incident/components/src/lib/core/cerrix-select/cerrix-select.component";
import { CerrixTextareaComponent } from "@app/incident/components/src/lib/core/cerrix-textarea/cerrix-textarea.component";
import { SelectOptionModel } from "@app/incident/components/src/lib/core/models/select-option.model";

@Component({
    selector: "cerrix-edit-document-incidents",
    templateUrl: "./cerrix-edit-document-incidents.component.html",
    styleUrls: ["./cerrix-edit-document-incidents.component.scss"],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatButtonModule,
        MatDialogModule,
        MatTabsModule,
        CerrixAccordionGroupComponent,
        CerrixButtonComponent,
        CerrixTitleComponent,
        CerrixFieldWrapperComponent,
        CerrixSelectComponent,
        CerrixTextareaComponent,
        CerrixTextareaComponent,
        CerrixDropAreaComponent,
        CerrixConfirmationButtonComponent,
        CerrixEditDocumentHistoryItemComponent,
        DateTimeDisplayPipe,
    ],
    providers: [{ provide: BaseDocumentsService, useClass: IncidentDocumentsService }]
})
export class CerrixEditDocumentIncidentComponent
    extends AbstractEditDocumentComponent
    implements OnInit
{
    protected dialogRef: MatDialogRef<EditDocumentPopup, DocumentModel[]> = inject(MatDialogRef);
    @Output() public emitDownload = new EventEmitter<DocumentModel>();
    @Output() public emitDelete = new EventEmitter<DocumentModel>();

    private documentsService = inject(BaseDocumentsService);

    protected historyDocuments: DocumentModel[] = [];

    ngOnInit(): void {
        super.ngOnInit();
    }

    protected onFileSelection(selection: { fileList: FileList; isBrowse: boolean }) {
        if (selection.fileList?.length) {
            const files = this.filterAllowedFiles(selection.fileList);

            if (files.length && this.validateUploadLimit(files)) {
                const newDocuments: DocumentModel[] = [];

                if (this.dialogData.openReason === OpenDocumentPopupReason.edit) {
                    this.dialogData.documents = [
                        {
                            name: files[0].name,
                            file: files[0],
                            fileIcon: this.getDocumentIcon(files[0].name),
                        },
                    ];
                } else {
                    files.forEach((file) => {
                        newDocuments.push({
                            name: file.name,
                            file: file,
                            fileIcon: this.getDocumentIcon(file.name),
                        });
                    });

                    this.dialogData.documents = [...newDocuments];
                }
            }
        }
    }

    protected onRemoveFile(document: DocumentModel) {
        let index = this.dialogData.documents.findIndex((d) => d === document);
        this.dialogData.documents.splice(index, 1);
        this.emitDelete.emit(document);
        if (this.dialogData.showDelete) {
            this.onApplyChanges();
        }
    }

    protected onApplyChanges() {
        if (this.documentForm.valid) {
            this.dialogData.documents.forEach((document) => {
                document.documentTypeId = this.documentForm.value.documentTypeId;
                document.description = this.documentForm.value.description;
            });
            this.close(this.dialogData.documents);
        }
    }

    public onDownload(document: DocumentModel) {
        this.emitDownload.emit(document);
    }

    public getDocumentIcon(name: string) {
        return this.documentsService.getDocumentIcon(name);
    }

    public close(result: DocumentModel[]) {
        this.dialogRef.close(result);
    }

    public validateUploadLimit(files: File[]) {
        return this.documentsService.validateUploadLimit(
            files,
            this.documentConfig.maxUploadSizeInMb
        );
    }

    public filterAllowedFiles(files: FileList) {
        return this.documentsService.filterAllowedFiles(
            files,
            this.documentConfig.allowedFileExtensions
        );
    }

    getDocumentTypes(): Observable<SelectOptionModel[]> {
        return this.documentsService.getDocumentTypes();
    }
    handleDocumentTypes(documentTypes: SelectOptionModel[]): SelectOptionModel[] {
        return documentTypes.map((type) => {
            return { id: type.id, name: type.name };
        });
    }
    getDocumentConfig(): Observable<DocumentConfig> {
        return this.documentsService.getDocumentConfig();
    }
    handleDocumentConfig(documentConfig: DocumentConfig): DocumentConfig {
        return documentConfig;
    }
    getDocumentHistory(documentId: string): Observable<AbstractDocumentModel[]> {
        return this.documentsService.getDocumentHistory(documentId);
    }
    handleDocumentHistory(history: AbstractDocumentModel[]): AbstractDocumentModel[] {
        return history.map((doc) => ({
            ...doc,
            documentTypeId:
                this.documentTypes.find((type) => type.id === doc.documentTypeId)?.name || "-",
        }));
    }
}
